<template>
    <PageLayoutForms>
        <template #header>
            <h1 class="pdp-mandate-onboarding-electronic-address__title typographies-heading-h1">
                {{ $t.__('pdp.mandate.Onboarding.title') }}
            </h1>
        </template>
        <template #content>
            <CompassInformationBanner
                :title="$t.__('pdp.mandate.OnboardingElectronicAddress.InformationBanner.title')"
                sourceIllustration="/ressources/logged/img/illustrations/pdp/receipt-purchases.svg"
            >
                <template #description>
                    <div
                        class="pdp-mandate-onboarding-electronic-address__information typographies-paragraph-medium-regular"
                        v-html="informationBannerDescription"
                    />
                </template>
            </CompassInformationBanner>
            <h2 class="pdp-mandate-onboarding-electronic-address__subtitle typographies-heading-h2">
                {{ $t.__('pdp.mandate.OnboardingElectronicAddress.subtitle') }}
            </h2>
            <div class="pdp-mandate-onboarding-electronic-address__choices">
                <OnboardingChoice
                    v-for="choice in ['sellsy', 'other']"
                    :key="choice"
                    :type="choice"
                    :state="choiceState(choice)"
                    @select="handleChoiceSelection"
                />
            </div>
            <div
                v-if="selectedAddressType"
                class="pdp-mandate-onboarding-electronic-address__creation"
            >
                <h3 class="typographies-paragraph-large-bold">
                    {{ $t.__('pdp.mandate.OnboardingElectronicAddress.creation.title') }}
                </h3>
                <div
                    class="pdp-mandate-onboarding-electronic-address__creation-wrapper typographies-paragraph-medium-regular"
                >
                    <template v-if="!editMode">
                        <span class="pdp-mandate-onboarding-electronic-address__creation-value">
                            {{ placeholder }}
                        </span>
                        <CompassIconButton icon="edit" @click="toggleEditMode" />
                    </template>
                    <template v-else>
                        <span class="pdp-mandate-onboarding-electronic-address__creation-prefix">
                            {{ formattedSiren }}_
                        </span>
                        <CompassFormItem
                            class="pdp-mandate-onboarding-electronic-address__creation-input"
                            label=""
                            :error="isError ? errorMessage : ''"
                        >
                            <CompassInput
                                ref="inputElectronicAddress"
                                v-model="temporaryElectronicAddress"
                                :placeholder="inputPlaceholder"
                                :error="isError"
                                @keyup.native.enter="validateElectronicAddress"
                                @keyup.native.esc="toggleEditMode"
                            />
                        </CompassFormItem>
                        <CompassIconButton
                            icon="close"
                            :disabled="isCancelButtonDisabled"
                            @click="toggleEditMode"
                        />
                        <CompassIconButton
                            icon="check"
                            type="primary"
                            :disabled="isValidateButtonDisabled"
                            @click="validateElectronicAddress"
                        />
                    </template>
                </div>
                <div
                    class="pdp-mandate-onboarding-electronic-address__creation-information typographies-paragraph-medium-regular"
                >
                    {{ creationInformation }}
                </div>
                <CompassPlainButton
                    :label="$t.__('pdp.mandate.OnboardingElectronicAddress.support_button')"
                    @click="goToFAQ"
                />
            </div>
        </template>
        <template #footer>
            <FooterFullPage :primaryAction="primaryAction" :secondaryActions="secondaryActions" />
        </template>
    </PageLayoutForms>
</template>
<script>
import FooterFullPage from 'vcomponents/common/FooterFullPage/FooterFullPage.vue';
import OnboardingChoice from 'vcomponents/pa/mandate/Onboarding/OnboardingChoice.vue';
import PageLayoutForms from 'vcomponents/common/layout/pages/PageLayoutForms.vue';

export default {
    components: {
        FooterFullPage,
        OnboardingChoice,
        PageLayoutForms,
    },
    props: {
        siren: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            primaryAction: {
                label: `${this.$t.__('common.actions.continue')} (2/2)`,
                disabled: true,
                action: () => {
                    this.$emit('addressSelected', this.formattedElectronicAddress);
                },
            },
            secondaryActions: [
                {
                    label: this.$t.__('common.actions.cancel'),
                    action: () => {
                        this.$router.push({ name: 'dashboard' });
                    },
                },
            ],
            informationBannerDescription: this.$t.__(
                'pdp.mandate.OnboardingElectronicAddress.InformationBanner.description',
                {
                    paragraphStart: '<p>',
                    paragraphEnd: '</p>',
                    boldStart: '<strong>',
                    boldEnd: '</strong>',
                },
            ),
            selectedAddressType: null,
            form: {
                electronicAddress: '',
            },
            isError: false,
            errorMessage: '',
            temporaryElectronicAddress: '',
            editMode: false,
        };
    },
    computed: {
        creationInformation() {
            if (this.selectedAddressType === 'sellsy') {
                return this.$t.__(
                    'pdp.mandate.OnboardingElectronicAddress.creation.information.sellsy',
                );
            }
            return this.$t.__('pdp.mandate.OnboardingElectronicAddress.creation.information.other');
        },
        inputPlaceholder() {
            return this.selectedAddressType === 'other'
                ? this.$t.__('pdp.mandate.OnboardingElectronicAddress.creation.placeholder')
                : '';
        },
        formattedSiren() {
            return this.siren.replaceAll(' ', '');
        },
        formattedElectronicAddress() {
            return this.form.electronicAddress
                ? `${this.formattedSiren}_${this.form.electronicAddress}`
                : this.formattedSiren;
        },
        isCancelButtonDisabled() {
            if (this.selectedAddressType === 'sellsy') {
                return false;
            }
            return !this.temporaryElectronicAddress && !this.form.electronicAddress;
        },
        isValidateButtonDisabled() {
            if (this.selectedAddressType === 'sellsy') {
                return false;
            }
            return !this.temporaryElectronicAddress;
        },
        placeholder() {
            return this.form.electronicAddress
                ? `${this.formattedSiren}_${this.form.electronicAddress}`
                : `${this.formattedSiren}`;
        },
    },
    methods: {
        choiceState(type) {
            if (!this.selectedAddressType) {
                return 'default';
            }
            return this.selectedAddressType === type ? 'selected' : 'unselected';
        },
        handleChoiceSelection(choice) {
            this.selectedAddressType = choice;
            if (choice === 'other') {
                this.editMode = true;
                this.$nextTick(() => {
                    this.$refs.inputElectronicAddress.setFocus();
                });
            } else {
                this.editMode = false;
            }
            this.temporaryElectronicAddress = '';
            this.isError = false;
            this.form.electronicAddress = this.temporaryElectronicAddress;
            if (choice === 'sellsy') {
                this.primaryAction.disabled = false;
            } else {
                this.primaryAction.disabled = !this.form.electronicAddress;
            }
        },
        toggleEditMode() {
            this.editMode = !this.editMode;
            if (this.editMode) {
                this.$nextTick(() => {
                    this.$refs.inputElectronicAddress.setFocus();
                });
            }
            this.primaryAction.disabled =
                (!this.form.electronicAddress && this.selectedAddressType !== 'sellsy') ||
                this.editMode;
            this.temporaryElectronicAddress = this.form.electronicAddress;
            this.isError = false;
            this.errorMessage = '';
        },
        validateElectronicAddress() {
            if (
                !this.temporaryElectronicAddress.match(/^[a-zA-Z0-9_-]*$/) ||
                this.temporaryElectronicAddress.length > 99
            ) {
                this.isError = true;
                this.errorMessage =
                    this.temporaryElectronicAddress.length > 99
                        ? this.$t.__(
                              'pdp.mandate.OnboardingElectronicAddress.creation.errors.length',
                          )
                        : this.$t.__(
                              'pdp.mandate.OnboardingElectronicAddress.creation.errors.special_characters',
                          );
                return;
            }
            this.isError = false;
            this.form.electronicAddress = this.temporaryElectronicAddress;
            this.toggleEditMode();
        },
        goToFAQ() {
            window.open(
                'https://help.sellsy.com/fr/articles/12674837-completer-le-mandat-d-emission-et-de-reception-sur-la-plateforme-sellsy-pa',
                '_blank',
            );
        },
    },
};
</script>
<style lang="scss">
body:has(.pdp-mandate-onboarding-electronic-address) {
    #sellsy_app {
        background-color: var(--colors-common-background-default);
    }
}
</style>
<style lang="scss" scoped>
.pdp-mandate-onboarding-electronic-address {
    :deep(.information-banner) {
        margin-bottom: var(--spacing-4);
    }

    &__information {
        color: var(--colors-common-text-brand-secondary-blue);

        :deep(p) {
            margin: 0;

            & + p {
                margin: var(--spacing-3) 0 0;
            }
        }
    }

    &__choices {
        display: flex;
        gap: var(--spacing-4);
    }

    &__creation {
        &-prefix {
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: var(--spacing-5);
        }

        &-wrapper {
            align-items: start;
            background: var(--colors-common-background-subtle);
            border-radius: var(--spacing-1);
            display: flex;
            gap: 10px;
            padding: var(--spacing-2);

            :deep() {
                .compass-icon-button {
                    flex-shrink: 0;
                }
            }
        }
        &-input {
            flex: 1;
        }

        &-value {
            display: flex;
            flex-direction: column;
            flex: 1;
            justify-content: center;
            min-height: var(--spacing-5);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &-information {
            color: var(--colors-common-text-subtle-default);
            margin: var(--spacing-0) 0;
        }
    }
}
</style>
